import { ListboxOption } from "@reach/listbox";
import classNames from "classnames";
import { ButtonPrimary, ButtonText } from "components/Clickable/Buttons";
import { CmsResponsiveImage } from "components/CmsResponsiveImage";
import { Dropdown } from "components/Dropdown/Dropdown";
import { NewList } from "components/Favorites/NewList";
import {
    PDPFavoriteItem,
    PDPFavoriteItemApi,
    RulesetFavoriteItem,
    RulesetFavoriteItemApi,
    SelectedProduct as SelectedProductProps,
} from "components/Favorites/interfaces";
import { ErrorMessage } from "components/Input/ErrorMessage";
import {
    usePartiallyConfiguredProduct,
    usePartiallyConfiguredRuleset,
} from "components/productContext";
import { writeCMSDataClientSide } from "helpers/api";
import { processServerSideErrors } from "helpers/forms";
import urls from "helpers/urls";
import { getProductImageTransforms } from "helpers/utils";
import { FormBase } from "interfaces";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import styles from "./add-to-favorites-modal.module.scss";

type ModalIntent = "Add" | "Move" | "Copy";

export interface Props {
    labelId: string;
    intent: ModalIntent;
    emitResponse?: (item: PDPFavoriteItem | RulesetFavoriteItem) => void;
}

interface AddToExistingListForm extends FormBase {
    list: string;
}

export const AddToFavoritesPDPContent = ({
    labelId,
    intent,
    emitResponse,
}: Props) => {
    const [addedToList, setAddedToList] = useState<{
        title: string;
        url: string;
    } | null>(null);

    const selectedProduct = usePartiallyConfiguredProduct();
    const favorites = selectedProduct.favoritesLists;
    const addToListImmediately = selectedProduct.userStatus === "lazy";

    const onSubmit: SubmitHandler<AddToExistingListForm> = async (data) => {
        const list = favorites.find(
            (list) => list.id === parseInt(data.list, 10),
        )!;

        const method =
            intent === "Move" && selectedProduct.favoriteItem ? "PUT" : "POST";
        const url =
            method === "PUT"
                ? `/api/favorites-item/${selectedProduct.favoriteItem!.id}`
                : `/api/favorites-item/`;

        const response = await writeCMSDataClientSide(
            method,
            url,
            {},
            {
                ...request,
                lists: [list.id],
            },
        );

        if (response.ok) {
            (response.json() as Promise<PDPFavoriteItem>).then((response) => {
                setAddedToList({
                    title: list.title,
                    url: urls.favoriteListPage(list.id),
                });
                /**
                 * TODO make this less fragile
                 * At time of writing, if intent isn't in here, this will emit on move
                 * and delete the thing that's moved
                 */
                emitResponse && intent === "Copy" && emitResponse(response);
            });
        } else {
            processServerSideErrors(response, data, setError);
        }
    };

    useEffect(() => {
        if (addToListImmediately) {
            const data: AddToExistingListForm = {
                list: favorites[0].id.toString(),
                server: null,
            };

            onSubmit(data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        clearErrors,
        control,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<AddToExistingListForm>();

    if (
        (intent === "Move" || intent === "Copy") &&
        !selectedProduct.favoriteItem
    ) {
        console.log(
            "This component needs existing favorite item data in order to move or copy a favorites item.",
        );
        return <></>;
    }

    if (
        intent === "Add" &&
        (!selectedProduct.favoriteItemPieces || !selectedProduct.details)
    ) {
        console.log(`
      This component needs favoriteItemPieces and details in ProductProvider in order to
      add a favorites item to a list.
    `);
        return <></>;
    }

    let request: PDPFavoriteItemApi | null = null;
    if (!!selectedProduct.favoriteItem) {
        request = {
            title: selectedProduct.favoriteItem.title,
            notes: selectedProduct.favoriteItem.notes,
            page: selectedProduct.favoriteItem.page,
            choices: selectedProduct.favoriteItem.choices,
            lists: [],
            is_ruleset_favorite: false,
        };
    }
    if (!!selectedProduct.favoriteItemPieces && !!selectedProduct.details) {
        request = {
            title: selectedProduct.details.title,
            notes: null,
            page: selectedProduct.favoriteItemPieces.page,
            choices: selectedProduct.favoriteItemPieces.choices,
            lists: [],
            is_ruleset_favorite: false,
        };
    }

    if (request === null) {
        console.log(`
      Something's not right. Make sure you're passing in the right stuff via ProductProvider.
    `);
        return <></>;
    }

    let title = "";
    if (!!selectedProduct.pageTitle) {
        if (!!selectedProduct.pageTitle.pageTitle)
            title = selectedProduct.pageTitle.pageTitle;
        if (!!selectedProduct.pageTitle.configString)
            title = selectedProduct.pageTitle.configString;
    }
    if (!!selectedProduct.favoriteItem)
        title = selectedProduct.favoriteItem.config_string;

    return (
        <>
            <h1 id={labelId} className={classNames(["h4", styles.heading])}>
                {intent === "Add" && "Add to Favorite"}
                {intent === "Move" && "Move To"}
                {intent === "Copy" && "Copy To"}
            </h1>
            <SelectedProduct
                title={title}
                image={
                    selectedProduct.favoriteItem
                        ? selectedProduct.favoriteItem.image
                        : selectedProduct.favoriteItemPieces!.image
                }
                image_padding={
                    selectedProduct.favoriteItem?.image_padding ?? "NORMAL"
                }
            />
            {!!addedToList && (
                <div className={styles.added}>
                    <p>
                        {intent === "Add" && "Added"}
                        {intent === "Move" && "Moved"}
                        {intent === "Copy" && "Copied"} to “{addedToList.title}”
                    </p>
                    <ButtonText href={addedToList.url}>Go To List</ButtonText>
                </div>
            )}
            {!addedToList && selectedProduct.userStatus === "lazy" && (
                <div className={styles.added}>
                    <p>Adding to your list…</p>
                </div>
            )}
            {!addedToList && selectedProduct.userStatus !== "lazy" && (
                <>
                    {favorites.length > 0 && (
                        <form
                            noValidate
                            onSubmit={(event) => {
                                clearErrors();
                                handleSubmit(onSubmit)(event);
                            }}
                        >
                            <section className={styles.favoritesList}>
                                <Controller
                                    name="list"
                                    control={control}
                                    defaultValue={favorites[0].id.toString()}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <Dropdown
                                            value={value}
                                            label="My Favorites"
                                            onChange={onChange}
                                            popoverPortal={false}
                                        >
                                            {favorites.map(({ id, title }) => (
                                                <ListboxOption
                                                    key={id}
                                                    value={`${id}`}
                                                >
                                                    {title}
                                                </ListboxOption>
                                            ))}
                                        </Dropdown>
                                    )}
                                />
                                <ButtonPrimary
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                    type="submit"
                                    className={classNames({
                                        [styles.addToList]: true,
                                        "al-pdp-fav__a2l": intent === "Add",
                                        "al-pdp-fav__m2l": intent === "Move",
                                        "al-pdp-fav__c2l": intent === "Copy",
                                    })}
                                >
                                    {intent} To List
                                </ButtonPrimary>
                                {errors.server?.message && (
                                    <ErrorMessage>
                                        {errors.server?.message}
                                    </ErrorMessage>
                                )}
                            </section>
                            <div className={styles.divider}>
                                <span>Or</span>
                            </div>
                        </form>
                    )}
                    <NewList
                        className={styles.newListModal}
                        ctaText={`Create & ${intent} To List`}
                        placeholder="Enter list name"
                        request={request}
                        createFirstListImmediately={favorites.length === 0}
                        onSuccess={(newList) => {
                            selectedProduct.setFavoritesList &&
                                selectedProduct.setFavoritesList([
                                    ...favorites,
                                    newList,
                                ]);
                            if (
                                favorites.length === 0 &&
                                selectedProduct.userStatus === "unregistered" &&
                                selectedProduct.setUserStatus
                            ) {
                                selectedProduct.setUserStatus("lazy");
                            }
                            setAddedToList({
                                title: newList.title,
                                url: urls.favoriteListPage(newList.id),
                            });
                            intent === "Move" &&
                                emitResponse &&
                                emitResponse(newList.preview[0]);
                        }}
                    />
                </>
            )}
        </>
    );
};

// Very unDRY; to be combined/abstracted with the above later
export const AddToFavoritesRulesetContent = ({
    labelId,
    intent,
    emitResponse,
}: Props) => {
    const [addedToList, setAddedToList] = useState<{
        title: string;
        url: string;
    } | null>(null);

    const selectedProduct = usePartiallyConfiguredRuleset();

    const favorites = selectedProduct.favoritesLists;
    const addToListImmediately = selectedProduct.userStatus === "lazy";

    const onSubmit: SubmitHandler<AddToExistingListForm> = async (data) => {
        const list = favorites.find(
            (list) => list.id === parseInt(data.list, 10),
        )!;

        const method =
            intent === "Move" && selectedProduct.favoriteItem ? "PUT" : "POST";
        const url =
            method === "PUT"
                ? `/api/favorites-item/${selectedProduct.favoriteItem!.id}`
                : `/api/favorites-item/`;

        const response = await writeCMSDataClientSide(
            method,
            url,
            {},
            {
                ...request,
                lists: [list.id],
            },
        );

        if (response.ok) {
            (response.json() as Promise<RulesetFavoriteItem>).then(
                (response) => {
                    setAddedToList({
                        title: list.title,
                        url: urls.favoriteListPage(list.id),
                    });
                    /**
                     * TODO make this less fragile
                     * At time of writing, if intent isn't in here, this will emit on move
                     * and delete the thing that's moved
                     */
                    emitResponse && intent === "Copy" && emitResponse(response);
                },
            );
        } else {
            processServerSideErrors(response, data, setError);
        }
    };

    useEffect(() => {
        if (addToListImmediately) {
            const data: AddToExistingListForm = {
                list: favorites[0].id.toString(),
                server: null,
            };

            onSubmit(data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        clearErrors,
        control,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<AddToExistingListForm>();

    if (
        (intent === "Move" || intent === "Copy") &&
        !selectedProduct.favoriteItem
    ) {
        console.log(
            "This component needs existing favorite item data in order to move or copy a favorites item.",
        );
        return <></>;
    }

    if (intent === "Add" && !selectedProduct.favoriteItemPieces) {
        console.log(`
      This component needs favoriteItemPieces and details in ProductProvider in order to
      add a favorites item to a list.
    `);
        return <></>;
    }

    let request: RulesetFavoriteItemApi | null = null;
    if (!!selectedProduct.favoriteItem) {
        request = {
            is_ruleset_favorite: true,
            title: selectedProduct.favoriteItem.title,
            notes: selectedProduct.favoriteItem.notes,
            page: selectedProduct.favoriteItem.page,
            choices: selectedProduct.favoriteItem.choices,
            lists: [],
        };
    }

    if (!!selectedProduct.favoriteItemPieces) {
        request = {
            is_ruleset_favorite: true,
            title: selectedProduct.favoriteItemPieces.title,
            notes: null,
            page: selectedProduct.favoriteItemPieces.page,
            choices: selectedProduct.favoriteItemPieces.choices,
            lists: [],
        };
    }

    if (request === null) {
        console.log(`
      Something's not right. Make sure you're passing in the right stuff via ProductProvider.
    `);
        return <></>;
    }

    let title = "";
    if (
        !!selectedProduct.favoriteItemPieces &&
        !!selectedProduct.favoriteItemPieces.title
    ) {
        title = selectedProduct.favoriteItemPieces.title;
    } else if (!!selectedProduct.favoriteItem)
        title = selectedProduct.favoriteItem.title;

    return (
        <>
            <h1 id={labelId} className={classNames(["h4", styles.heading])}>
                {intent === "Add" && "Add to Favorite"}
                {intent === "Move" && "Move To"}
                {intent === "Copy" && "Copy To"}
            </h1>
            <SelectedProduct
                title={title}
                image={
                    selectedProduct.favoriteItem
                        ? selectedProduct.favoriteItem.image
                        : selectedProduct.favoriteItemPieces!.image
                }
                image_padding={
                    selectedProduct.favoriteItem?.image_padding ?? "NORMAL"
                }
            />
            {!!addedToList && (
                <div className={styles.added}>
                    <p>
                        {intent === "Add" && "Added"}
                        {intent === "Move" && "Moved"}
                        {intent === "Copy" && "Copied"} to “{addedToList.title}”
                    </p>
                    <ButtonText href={addedToList.url}>Go To List</ButtonText>
                </div>
            )}
            {!addedToList && selectedProduct.userStatus === "lazy" && (
                <div className={styles.added}>
                    <p>Adding to your list…</p>
                </div>
            )}
            {!addedToList && selectedProduct.userStatus !== "lazy" && (
                <>
                    {favorites.length > 0 && (
                        <form
                            noValidate
                            onSubmit={(event) => {
                                clearErrors();
                                handleSubmit(onSubmit)(event);
                            }}
                        >
                            <section className={styles.favoritesList}>
                                <Controller
                                    name="list"
                                    control={control}
                                    defaultValue={favorites[0].id.toString()}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <Dropdown
                                            value={value}
                                            label="My Favorites"
                                            onChange={onChange}
                                            popoverPortal={false}
                                        >
                                            {favorites.map(({ id, title }) => (
                                                <ListboxOption
                                                    key={id}
                                                    value={`${id}`}
                                                >
                                                    {title}
                                                </ListboxOption>
                                            ))}
                                        </Dropdown>
                                    )}
                                />
                                <ButtonPrimary
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                    type="submit"
                                    className={classNames({
                                        [styles.addToList]: true,
                                        "al-pdp-fav__a2l": intent === "Add",
                                        "al-pdp-fav__m2l": intent === "Move",
                                        "al-pdp-fav__c2l": intent === "Copy",
                                    })}
                                >
                                    {intent} To List
                                </ButtonPrimary>
                                {errors.server?.message && (
                                    <ErrorMessage>
                                        {errors.server?.message}
                                    </ErrorMessage>
                                )}
                            </section>
                            <div className={styles.divider}>
                                <span>Or</span>
                            </div>
                        </form>
                    )}
                    <NewList
                        className={styles.newListModal}
                        ctaText={`Create & ${intent} To List`}
                        placeholder="Enter list name"
                        request={request}
                        createFirstListImmediately={favorites.length === 0}
                        onSuccess={(newList) => {
                            selectedProduct.setFavoritesList &&
                                selectedProduct.setFavoritesList([
                                    ...favorites,
                                    newList,
                                ]);
                            if (
                                favorites.length === 0 &&
                                selectedProduct.userStatus === "unregistered" &&
                                selectedProduct.setUserStatus
                            ) {
                                selectedProduct.setUserStatus("lazy");
                            }
                            setAddedToList({
                                title: newList.title,
                                url: urls.favoriteListPage(newList.id),
                            });
                            intent === "Move" &&
                                emitResponse &&
                                emitResponse(newList.preview[0]);
                        }}
                    />
                </>
            )}
        </>
    );
};

const SelectedProduct = ({
    image,
    title,
    image_padding,
}: SelectedProductProps) => (
    <figure className={styles.selectedProduct}>
        {image && (
            <CmsResponsiveImage
                cmsImage={{ url: image[0] }}
                sizes="100px"
                cldSrcSet={[100, 200, 300, 400]}
                cloudinaryProps={getProductImageTransforms(image_padding)}
            />
        )}
        <figcaption>
            <p>{title}</p>
        </figcaption>
    </figure>
);
